import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { webinarAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Search, Filter, Radio, Calendar, Grid3X3, List, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { motion, AnimatePresence } from 'framer-motion';
import { AppLayout } from '@/components/layout/AppLayout';
import WebinarCard from '@/components/webinars/WebinarCard';

export default function WebinarListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');

  const isHost = user?.role === 'admin' || user?.role === 'trainer';

  const fetchWebinars = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const { data } = await webinarAPI.list(params);
      setWebinars(data);
    } catch (err) {
      toast.error('Erreur lors du chargement des webinaires');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchWebinars(); }, [fetchWebinars]);

  const filtered = webinars.filter(w =>
    w.title?.toLowerCase().includes(search.toLowerCase()) ||
    w.description?.toLowerCase().includes(search.toLowerCase()) ||
    w.category?.toLowerCase().includes(search.toLowerCase())
  );

  const liveCount = webinars.filter(w => w.status === 'live').length;
  const scheduledCount = webinars.filter(w => w.status === 'scheduled').length;

  const handleEdit = (w) => navigate(`/admin/webinars/${w.id}/edit`);
  const handleJoin = (w) => navigate(`/admin/webinars/${w.id}/live`);
  const handleStart = async (w) => {
    try {
      await webinarAPI.start(w.id);
      toast.success('Webinaire démarré');
      navigate(`/admin/webinars/${w.id}/live`);
    } catch { toast.error('Erreur de démarrage'); }
  };
  const handleAnalytics = (w) => navigate(`/admin/webinars/${w.id}/analytics`);
  const handleDuplicate = async (w) => {
    try {
      await webinarAPI.duplicate(w.id);
      toast.success('Webinaire dupliqué');
      fetchWebinars();
    } catch { toast.error('Erreur de duplication'); }
  };
  const handleDelete = async (w) => {
    if (!window.confirm(`Supprimer "${w.title}" ?`)) return;
    try {
      await webinarAPI.delete(w.id);
      toast.success('Webinaire supprimé');
      fetchWebinars();
    } catch { toast.error('Erreur de suppression'); }
  };

  return (
    <AppLayout>
      <div className="min-h-screen bg-zinc-50">
        {/* Header */}
      <div className="border-b border-zinc-200 bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-violet-600 flex items-center justify-center">
                <Video className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-zinc-900">Webinaires & Live</h1>
                <p className="text-xs text-zinc-500">{webinars.length} webinaire{webinars.length > 1 ? 's' : ''}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {liveCount > 0 && (
                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500/10 text-red-400 text-xs font-semibold animate-pulse border border-red-500/20">
                  <Radio className="w-3 h-3" /> {liveCount} en direct
                </span>
              )}
              {scheduledCount > 0 && (
                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-500/10 text-blue-400 text-xs font-medium border border-blue-500/20">
                  <Calendar className="w-3 h-3" /> {scheduledCount} planifié{scheduledCount > 1 ? 's' : ''}
                </span>
              )}
              {isHost && (
                <Button onClick={() => navigate(`/admin/webinars/new`)}
                  className="bg-gradient-to-r from-cyan-600 to-violet-600 hover:from-cyan-700 hover:to-violet-700 text-white gap-2 shadow-lg shadow-cyan-500/10">
                  <Plus className="w-4 h-4" /> Nouveau webinaire
                </Button>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Rechercher un webinaire..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-white border-zinc-200 text-sm h-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 bg-white border-zinc-200 text-sm h-9">
                <Filter className="w-3.5 h-3.5 mr-1.5 text-zinc-500" />
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent className="bg-white border-zinc-200">
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="draft">Brouillons</SelectItem>
                <SelectItem value="scheduled">Planifiés</SelectItem>
                <SelectItem value="live">En direct</SelectItem>
                <SelectItem value="ended">Terminés</SelectItem>
              </SelectContent>
            </Select>
            <Tabs value={viewMode} onValueChange={setViewMode} className="hidden md:block">
              <TabsList className="bg-zinc-100 h-9">
                <TabsTrigger value="grid" className="px-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"><Grid3X3 className="w-4 h-4 text-zinc-600" /></TabsTrigger>
                <TabsTrigger value="list" className="px-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"><List className="w-4 h-4 text-zinc-600" /></TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {loading ? (
          <div className={`grid ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'} gap-4`}>
            {[...Array(6)].map((_, i) => (
              <div key={i} className="rounded-xl border border-zinc-200 bg-white overflow-hidden animate-pulse">
                <div className="h-36 bg-zinc-100" />
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-zinc-100 rounded w-3/4" />
                  <div className="h-3 bg-zinc-100 rounded w-1/2" />
                  <div className="h-3 bg-zinc-100 rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center py-20 text-zinc-500">
            <Video className="w-16 h-16 mb-4 text-zinc-300" />
            <p className="text-lg font-medium text-zinc-700">Aucun webinaire trouvé</p>
            <p className="text-sm mt-1">Commencez par créer votre premier webinaire</p>
            {isHost && (
              <Button onClick={() => navigate(`/admin/webinars/new`)}
                className="mt-4 bg-gradient-to-r from-cyan-600 to-violet-600 gap-2">
                <Plus className="w-4 h-4" /> Créer un webinaire
              </Button>
            )}
          </motion.div>
        ) : (
          <div className={`grid ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'} gap-4`}>
            <AnimatePresence>
              {filtered.map(w => (
                <WebinarCard
                  key={w.id}
                  webinar={w}
                  isHost={isHost || w.created_by === user?.id}
                  userRole={user?.role}
                  onEdit={handleEdit}
                  onJoin={handleJoin}
                  onStart={handleStart}
                  onAnalytics={handleAnalytics}
                  onDuplicate={handleDuplicate}
                  onDelete={handleDelete}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
    </AppLayout>
  );
}
